import React, { useEffect, useMemo, useState } from "react";
import "./AccessControl.css";
import Table from "../../../components/reusable-table/Table";
import Input from "../../../components/reusable-input/Input";
import { SearchIcon, MoreVerticalIcon } from "../../../icons/reactIcons";
import StatusChip from "../../../components/custom-statusChip/StatusChip";
import AccessControlModal from "./modals/AccessControlModal";
import MenuItem from "../../../components/reusable-menuitem/MenuItem";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import {
  fetchAdminListThunk,
  createAdminThunk,
  updateAdminThunk,
  fetchRoleListThunk,
  resetAdminPasswordThunk,
} from "../../../redux/slices/accessControlSlice";
import { formatDateTime } from "../../../utils/formatDateTime";
import { formatRole } from "../../../utils/formatRole";
import { formatName } from "../../../utils/formatName";
import ConfirmUserActionModal from "../../../components/confirm-modal/ConfirmUserActionModal";

const columns = [
  { header: "Name", accessor: "name" },
  { header: "Email", accessor: "email", className: "no-capitalize" },
  { header: "Created By", accessor: "createdBy" },
  { header: "Updated By", accessor: "updatedBy" },
];

const AccessControl: React.FC = () => {
  const dispatch = useAppDispatch();
  const { admins, listLoading } = useAppSelector((state) => state.admin);

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "reset">("add");
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [menuRow, setMenuRow] = useState<any>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  }>({ top: 0, right: 0 });
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchAdminListThunk());
    dispatch(fetchRoleListThunk());
  }, [dispatch]);

  const handleCreateNew = () => {
    setModalMode("add");
    setSelectedRow(null);
    setIsModalOpen(true);
  };

  const handleEdit = (row: any) => {
    setModalMode("edit");

    const rawRow = admins.find((a) => a.id === row.id);
    setSelectedRow(rawRow);

    setIsModalOpen(true);
  };

  const handleFormSubmit = async (formData: any) => {
    if (modalMode === "add") {
      const payload = {
        roleId: formData.roleId,
        name: formData.name,
        email: formData.email,
        password: formData.password,
        isActive: true,
      };

      const resultAction = await dispatch(createAdminThunk(payload));

      if (createAdminThunk.fulfilled.match(resultAction)) {
        setIsModalOpen(false);
        dispatch(fetchAdminListThunk());
      }
    } else if (modalMode === "edit") {
      const payload = {
        id: selectedRow.id,
        roleId: formData.roleId,
        name: formData.name,
        email: formData.email,
        isActive: selectedRow.isActive,
      };

      const resultAction = await dispatch(updateAdminThunk(payload));

      if (updateAdminThunk.fulfilled.match(resultAction)) {
        setIsModalOpen(false);
        dispatch(fetchAdminListThunk());
      }
    } else if (modalMode === "reset") {
      const payload = {
        id: selectedRow.id,
        password: formData.password,
      };

      const resultAction = await dispatch(resetAdminPasswordThunk(payload));

      if (resetAdminPasswordThunk.fulfilled.match(resultAction)) {
        setIsModalOpen(false);
        dispatch(fetchAdminListThunk());
      }
    }
  };

  const handleMenuOpen = (e: React.MouseEvent, row: any) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuRow(row);
    setMenuPosition({ top: rect.top + window.scrollY, right: 140 });
  };

  const handleDeleteClick = (row: any) => {
    const rawRow = admins.find((a) => a.id === row.id);
    setSelectedRow(rawRow);
    setOpenDeleteModal(true);
  };

  const handleResetClick = (row: any) => {
    const rawRow = admins.find((a) => a.id === row.id);
    setSelectedRow(rawRow);
    setModalMode("reset");
    setIsModalOpen(true);
  };

  const handleDeleteQuestion = async () => {
    if (!selectedRow) return;

    try {
      setSubmitting(true);

      const payload = {
        id: selectedRow.id,
        roleId: selectedRow.role?.id,
        name: selectedRow.name,
        email: selectedRow.email,
        isActive: false, // ✅ delete means deactivate
      };

      const resultAction = await dispatch(updateAdminThunk(payload));

      if (updateAdminThunk.fulfilled.match(resultAction)) {
        setOpenDeleteModal(false);
        setMenuRow(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const actions = [
    {
      label: <MoreVerticalIcon />,
      onClick: (row: any, e: React.MouseEvent) => handleMenuOpen(e, row),
      className: "more-btn",
    },
  ];

  const menuOptions = menuRow
    ? [
        { label: "Edit", onClick: () => handleEdit(menuRow.raw) },
        {
          label: "Reset Password",
          onClick: () => handleResetClick(menuRow.raw),
        },
        { label: "Delete", onClick: () => handleDeleteClick(menuRow.raw) },
      ]
    : [];

  const tableData = useMemo(() => {
    return admins
      .filter((admin) =>
        admin.email.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      .map((admin) => ({
        id: admin.id,
        email: admin.email,
        raw: admin,

        name: (
          <div className="name-with-chip">
            <span>{formatName(admin.name)}</span>
            <StatusChip
              status={formatRole(admin.role?.name) || "Admin"}
              color={admin.role?.name === "admin" ? "#A51843" : "#7A5C00"}
              backgroundcolor={
                admin.role?.name === "admin" ? "#FFE6F0" : "#FFF8E1"
              }
            />
          </div>
        ),
        createdBy: (
          <div className="merged-cell">
            <div className="merged-label">
              {formatName(admin.creator?.name) || "—"}
            </div>
            <div className="merged-date">{formatDateTime(admin.createdAt)}</div>
          </div>
        ),
        updatedBy: (
          <div className="merged-cell">
            <div className="merged-label">
              {formatName(admin.updater?.name) || "—"}
            </div>
            <div className="merged-date">{formatDateTime(admin.updatedAt)}</div>
          </div>
        ),
      }));
  }, [admins, searchTerm]);

  return (
    <div className="access-container">
      <div className="access-header">
        <h2>Access Control</h2>
        <div className="access-controls">
          <Input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            search
            startIcon={<SearchIcon fontSize={"20px"} />}
            removeBottomMargin
          />
          <button className="user-groups-btn" onClick={handleCreateNew}>
            Create New
          </button>
        </div>
      </div>

      <Table
        columns={columns}
        data={tableData}
        actions={actions}
        rowsPerPage={15}
        rounded
        loading={listLoading}
      />

      <MenuItem
        open={Boolean(menuRow)}
        onClose={() => setMenuRow(null)}
        options={menuOptions}
        style={{
          position: "absolute",
          top: `${menuPosition.top}px`,
          right: `${menuPosition.right}px`,
          zIndex: 1000,
        }}
      />

      <AccessControlModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode={modalMode}
        initialData={selectedRow}
        onSubmit={handleFormSubmit}
      />
      {openDeleteModal && (
        <ConfirmUserActionModal
          type="delete"
          userName={`${selectedRow?.email} access`}
          loading={submitting}
          onClose={() => setOpenDeleteModal(false)}
          onConfirm={handleDeleteQuestion}
        />
      )}
    </div>
  );
};

export default AccessControl;
